import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { useScrapeRuns, useStores, useTriggerScrape, useUpdateStore } from '../../lib/admin'
import type { AdminStore, ScrapeRun } from '../../lib/admintypes'
import { count } from '../../lib/format'
import { BUSINESS_TIMEZONE, shortDateTime } from '../../lib/datetime'

import { AdminLayout } from './AdminLayout'

/**
 * Stores and crawls (06 §2a, §3, §5).
 *
 * This screen exists so Andrew can answer one question in a glance — **is inventory healthy?** —
 * and then, if it is not, find out why without opening a shell.
 *
 * It used to answer a different question. Every store rendered as the same white card with the
 * same beige status pill, and the only date on it was `last_ok_at` — "last good run". So a store
 * whose last three crawls had failed showed a reassuring timestamp from Tuesday, and the run
 * table below it rendered `ok`, `partial` and `failed` in three shades of the same brand tint.
 * The information was all present and none of it was legible.
 *
 * Three things changed:
 *
 * * **Health leads.** The status a card shows is derived from the *most recent* run, plus the
 *   cooldown flag, plus mode. `last_ok_at` is still there, one line down, where reassurance
 *   belongs.
 * * **Severity has its own colour channel.** `--good/--warn/--bad`, orthogonal to the brand
 *   accent, used only on staff surfaces. A failing store is red at a glance from across a room.
 * * **Errors are grouped.** Twenty-five rows of "status 403" is one fact — the store refused
 *   us — and reading it as twenty-five distinct faults is how an operator concludes a store is
 *   broken in twenty-five ways.
 *
 * The numbers are three different questions and all three are shown, because they come apart:
 * *in catalogue* is what the crawl found, *approved* is what Andrew let through, and *in the
 * studio* is what a rail can actually show right now. A store can have a full catalogue and an
 * empty rail — every approved product priced eight days ago does exactly that.
 */

/** `Store.adapter` for a store that has no scraper because there is nothing to scrape. */
const NO_ADAPTER = 'manual'

const MODE_HELP: Record<string, string> = {
  scrape: 'Crawled with a browser on a schedule.',
  feed: 'Read from a licensed product feed. Needs a feed URL.',
  manual: 'Products are typed in or imported from a spreadsheet.',
  disabled: 'Off. The agent never sees products from this store.',
}

/** What a crawl error at each stage actually means, in Andrew's words rather than the pipeline's. */
const STAGE_HELP: Record<string, string> = {
  fetch: 'blocked or unreachable',
  incomplete: 'page had no price',
  parse: 'page did not parse',
  upsert: 'could not be saved',
  cooldown: 'resting after a refusal',
  run: 'the crawl itself failed',
  deactivate: 'refused to delist',
  feed: 'feed could not be read',
}

/** Share of a crawl's pages that may fail before the store is worth a second look. */
const NOISE_FLOOR = 0.05

type Tone = 'good' | 'warn' | 'bad' | 'idle'

interface Health {
  tone: Tone
  label: string
  detail: string
}

/**
 * One store's state, in the order the operator needs to hear it.
 *
 * Order matters more than the wording. "Paused" beats "failing" because a store Andrew turned
 * off is not a fault; "resting" beats "failing" because 06 §3.6 has us deliberately not
 * knocking; and a store that has never been crawled is neither healthy nor broken.
 */
function health(store: AdminStore): Health {
  if (!store.enabled || store.mode === 'disabled') {
    return { tone: 'idle', label: 'Paused', detail: 'Turned off. Nothing is crawled or shown.' }
  }
  if (store.mode === 'manual') {
    // No `detail`: `MODE_HELP.manual` says exactly this one line further down, and the card
    // printed both.
    return { tone: 'idle', label: 'Manual', detail: '' }
  }
  if (store.mode === 'feed' && !store.feed_url) {
    return {
      tone: 'warn',
      label: 'Needs a feed',
      detail: 'Set a feed URL and this starts working.',
    }
  }
  if (store.in_cooldown) {
    return {
      tone: 'warn',
      label: 'Resting',
      detail: 'The store refused us, so we stopped knocking for an hour.',
    }
  }
  if (store.last_status === 'running') {
    return { tone: 'idle', label: 'Crawling', detail: 'A crawl is in flight.' }
  }
  if (store.consecutive_failures >= 2) {
    return {
      tone: 'bad',
      label: 'Failing',
      detail: `The last ${store.consecutive_failures} crawls failed.`,
    }
  }
  if (store.last_status === 'failed') {
    return { tone: 'bad', label: 'Last crawl failed', detail: 'One failure. Worth a retry.' }
  }
  if (store.last_status === 'partial') {
    // Proportion, not presence. A big-box catalogue always has a few pages without a price, and
    // painting the whole store amber for one page in a hundred teaches an operator to ignore
    // the colour — which costs more than the errors do.
    const errors = Object.values(store.last_errors ?? {}).reduce((total, n) => total + n, 0)
    const rate = store.last_pages > 0 ? errors / store.last_pages : 1
    if (rate < NOISE_FLOOR) {
      return {
        tone: 'good',
        label: 'Healthy',
        detail: `The last crawl finished. ${errors} page${errors === 1 ? '' : 's'} of ${store.last_pages} did not — normal for a catalogue this size.`,
      }
    }
    return {
      tone: 'warn',
      label: 'Finished with errors',
      detail: `${errors} of ${store.last_pages} pages did not come back.`,
    }
  }
  if (store.last_status === 'ok') {
    return { tone: 'good', label: 'Healthy', detail: 'The last crawl finished cleanly.' }
  }
  return { tone: 'idle', label: 'Never crawled', detail: 'No crawl has run for this store yet.' }
}

const TONE_TEXT: Record<Tone, string> = {
  good: 'text-good',
  warn: 'text-warn',
  bad: 'text-bad',
  idle: 'text-muted',
}
const TONE_PILL: Record<Tone, string> = {
  good: 'bg-good-tint text-good',
  warn: 'bg-warn-tint text-warn',
  bad: 'bg-bad-tint text-bad',
  idle: 'bg-mist text-ink',
}
const TONE_STRIPE: Record<Tone, string> = {
  good: 'bg-good',
  warn: 'bg-warn',
  bad: 'bg-bad',
  idle: 'bg-line-2',
}

export function AdminStores() {
  const stores = useStores()
  const runs = useScrapeRuns()
  const trigger = useTriggerScrape()
  const [busy, setBusy] = useState<string | null>(null)

  const all = useMemo(() => stores.data ?? [], [stores.data])

  return (
    <AdminLayout>
      <h1 className="text-2xl">Stores</h1>
      <p className="mt-2 max-w-[70ch] text-sm text-muted">
        Where products come from. Crawls run overnight, one store at a time, at most one request a
        second — the schedule and the rate are per store so a supplier that asks us to slow down
        gets what they asked for.
      </p>

      <Summary stores={all} runs={runs.data ?? []} />

      <div className="mt-6 grid gap-4">
        {all.map((store) => (
          <StoreCard
            key={store.key}
            store={store}
            busy={busy === store.key}
            onScrape={async (kind, only) => {
              setBusy(store.key)
              try {
                await trigger.mutateAsync({ key: store.key, kind, only })
              } finally {
                setBusy(null)
              }
            }}
          />
        ))}
      </div>

      <RunTable runs={runs.data ?? []} />
    </AdminLayout>
  )
}

/**
 * The strip that answers "is inventory healthy?" without reading anything else.
 *
 * Four numbers, and the third is the one that matters most: **in the studio** is what a
 * homeowner can actually be shown. The others are upstream of it.
 */
function Summary({ stores, runs }: { stores: AdminStore[]; runs: ScrapeRun[] }) {
  const sellable = stores.reduce((total, store) => total + store.sellable_count, 0)
  const approved = stores.reduce((total, store) => total + store.whitelisted_count, 0)
  const crawled = stores.reduce((total, store) => total + store.product_count, 0)
  const slots = Math.max(0, ...stores.map((store) => store.slot_count))
  const unwell = stores.filter((store) => ['warn', 'bad'].includes(health(store).tone))
  const last = runs.find((run) => run.finished_at)

  const worst: Tone = unwell.some((store) => health(store).tone === 'bad')
    ? 'bad'
    : unwell.length
      ? 'warn'
      : 'good'

  return (
    <section
      aria-label="Inventory health"
      className="mt-6 overflow-hidden rounded-card border border-line bg-white"
    >
      <div className="grid gap-px bg-line sm:grid-cols-2 lg:grid-cols-4">
        <Tile
          label="In the studio"
          value={count(sellable)}
          note={`across ${slots} kinds of thing`}
        />
        <Tile label="Approved by you" value={count(approved)} note="whitelisted products" />
        <Tile label="In the catalogue" value={count(crawled)} note="everything crawled" />
        <Tile
          label="Stores"
          value={`${stores.length - unwell.length}/${stores.length}`}
          note={
            unwell.length ? `${unwell.map((s) => s.name).join(', ')} need attention` : 'all well'
          }
          tone={worst}
        />
      </div>
      {last && (
        <p className="m-0 border-t border-line bg-paper px-5 py-2.5 text-xs text-muted">
          Last finished crawl: <strong className="text-ink">{last.store_key}</strong>,{' '}
          {shortDateTime(last.finished_at ?? last.started_at, BUSINESS_TIMEZONE)}.
        </p>
      )}
    </section>
  )
}

function Tile({
  label,
  value,
  note,
  tone = 'idle',
}: {
  label: string
  value: string
  note: string
  tone?: Tone
}) {
  return (
    <div className="bg-white px-5 py-4">
      {/* `text-xs`, not `text-[0.7rem]`: 11.2 px is below the 12 px floor the visual suite
          enforces on every screen, and it caught this. */}
      <p className="m-0 font-mono text-xs uppercase tracking-[0.12em] text-muted">{label}</p>
      <p
        className={`m-0 mt-1 text-2xl font-semibold tabular-nums ${tone === 'idle' ? 'text-ink' : TONE_TEXT[tone]}`}
      >
        {value}
      </p>
      <p className="m-0 mt-0.5 text-xs text-muted">{note}</p>
    </div>
  )
}

/**
 * Catalogue size across the last dozen runs.
 *
 * 06 §5 asks for an alert on "a big drop". The alert fires by email; this is the same fact in a
 * shape, on the screen somebody is already looking at. A catalogue that halved overnight is
 * obvious here and invisible in a single number.
 */
function Sparkline({ values }: { values: number[] }) {
  if (values.length < 2) return null
  const width = 132
  const height = 28
  const max = Math.max(...values, 1)
  const step = width / (values.length - 1)
  const points: [number, number][] = values.map((value, index) => [
    index * step,
    height - (value / max) * height,
  ])
  const line = points.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ')
  const [lastX, lastY] = points.at(-1) ?? [0, 0]
  const latest = values.at(-1) ?? 0
  const previous = values.at(-2) ?? 0
  const dropped = previous > 0 && latest < previous * 0.7

  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      width={width}
      height={height}
      role="img"
      aria-label={`Products found by the last ${values.length} crawls: ${values.join(', ')}`}
      className="shrink-0 overflow-visible"
    >
      <polyline
        points={line}
        fill="none"
        stroke={dropped ? 'var(--bad)' : 'var(--blue)'}
        strokeWidth="1.5"
        strokeLinejoin="round"
        strokeLinecap="round"
      />
      <circle cx={lastX} cy={lastY} r="2.5" fill={dropped ? 'var(--bad)' : 'var(--blue)'} />
    </svg>
  )
}

function StoreCard({
  store,
  busy,
  onScrape,
}: {
  store: AdminStore
  busy: boolean
  onScrape: (kind: 'full' | 'prices', only?: string) => void
}) {
  const update = useUpdateStore()
  const [feed, setFeed] = useState(store.feed_url ?? '')
  const [only, setOnly] = useState('')
  const state = health(store)
  const errors = Object.entries(store.last_errors ?? {})

  return (
    <article className="relative overflow-hidden rounded-card border border-line bg-white pl-5">
      {/* A severity stripe, not a border colour: it reads from the edge of the eye while
          scanning a column of cards, which is exactly when it is needed. */}
      <span aria-hidden className={`absolute inset-y-0 left-0 w-1.5 ${TONE_STRIPE[state.tone]}`} />

      <div className="p-5 pl-4">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2.5">
              <h2 className="text-lg">{store.name}</h2>
              <span
                className={`rounded-pill px-2.5 py-1 text-xs font-semibold ${TONE_PILL[state.tone]}`}
              >
                {state.label}
              </span>
            </div>
            <p className="m-0 mt-1 font-mono text-xs text-muted">
              {store.key}
              {store.adapter === NO_ADAPTER
                ? ' · no scraper'
                : ` · ${store.adapter} · ${store.engine}`}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="flex items-center gap-2 text-sm text-ink">
              <input
                type="checkbox"
                checked={store.enabled}
                onChange={(event) =>
                  void update.mutateAsync({ key: store.key, enabled: event.target.checked })
                }
                className="size-4"
              />
              Enabled
            </label>
            <select
              value={store.mode}
              aria-label={`Mode for ${store.name}`}
              onChange={(event) =>
                void update.mutateAsync({ key: store.key, mode: event.target.value })
              }
              className="min-h-11 rounded-btn border border-line bg-white px-2.5 text-sm text-ink"
            >
              {/* A store with no adapter cannot be crawled, so the control does not offer it.
                  Builders Architectural Millwork is the case: their site is real and reachable
                  and has no prices on it anywhere, so there is nothing for a scraper to write.
                  Offering `scrape` here would be offering a mode that fails on the first
                  nightly run, hours later, in a log. */}
              {(store.adapter === NO_ADAPTER
                ? ['manual', 'disabled']
                : ['scrape', 'feed', 'manual', 'disabled']
              ).map((mode) => (
                <option key={mode} value={mode}>
                  {mode}
                </option>
              ))}
            </select>
          </div>
        </div>

        {state.detail && (
          <p
            className={`mt-2.5 text-sm ${state.tone === 'idle' ? 'text-muted' : TONE_TEXT[state.tone]}`}
          >
            {state.detail}
          </p>
        )}
        <p className="m-0 mt-1 text-sm text-muted">{MODE_HELP[store.mode]}</p>
        {store.note && <p className="mt-1 text-sm text-body">{store.note}</p>}

        <dl className="mt-4 grid grid-cols-2 gap-x-6 gap-y-3 text-sm sm:grid-cols-3 lg:grid-cols-5">
          <Fact
            label="In the studio"
            value={count(store.sellable_count)}
            note={`${store.slot_count} kinds`}
            tone={
              store.mode !== 'disabled' && store.enabled && store.sellable_count === 0
                ? 'warn'
                : 'idle'
            }
          />
          <Fact label="Approved" value={count(store.whitelisted_count)} />
          <Fact label="In the catalogue" value={count(store.product_count)} />
          {store.adapter !== NO_ADAPTER && (
            <>
              <Fact
                label="robots.txt"
                value={
                  store.robots_ok === null ? 'not read' : store.robots_ok ? 'read' : 'unreadable'
                }
              />
              <Fact
                label="Last good run"
                value={
                  store.last_ok_at ? shortDateTime(store.last_ok_at, BUSINESS_TIMEZONE) : 'never'
                }
              />
            </>
          )}
        </dl>

        {(store.recent_products.length > 1 || errors.length > 0) && (
          <div className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-2 rounded-btn bg-paper px-4 py-3">
            <Sparkline values={store.recent_products} />
            {store.recent_products.length > 1 && (
              <p className="m-0 text-xs text-muted">
                products found, last {store.recent_products.length} crawls
              </p>
            )}
            {errors.length > 0 && (
              <p
                className={`m-0 text-xs font-semibold ${TONE_TEXT[state.tone === 'good' ? 'warn' : state.tone]}`}
              >
                Last crawl:{' '}
                {errors.map(([stage, n]) => `${n}× ${STAGE_HELP[stage] ?? stage}`).join(' · ')}
              </p>
            )}
          </div>
        )}

        {store.api_terms && (
          <p className="mt-3 rounded-btn bg-paper px-3 py-2 text-xs text-body">
            <strong className="text-ink">API check:</strong> {store.api_available} —{' '}
            {store.api_terms}
          </p>
        )}

        {/* A store with no scraper gets the control that does work, not four greyed-out ones
            that never will. Three disabled buttons and a disabled text box is an invitation to
            keep trying. */}
        {store.adapter === NO_ADAPTER ? (
          <div className="mt-4 flex flex-wrap items-center gap-3">
            <Link
              to={`/admin/products?store=${store.key}`}
              className="grid min-h-11 place-items-center rounded-btn bg-navy px-4 text-sm font-semibold text-white"
            >
              Add products by hand
            </Link>
            <Link
              to="/admin/products"
              className="grid min-h-11 place-items-center rounded-btn border border-line bg-white px-4 text-sm font-semibold text-blue-deep"
            >
              Import a CSV
            </Link>
            <label className="flex items-center gap-2 text-xs text-muted">
              <span>Terms reviewed</span>
              <button
                type="button"
                onClick={() => void update.mutateAsync({ key: store.key, tos_reviewed: true })}
                className="min-h-10 rounded-btn border border-line bg-white px-3 text-xs font-semibold text-ink"
              >
                {store.tos_reviewed_at
                  ? shortDateTime(store.tos_reviewed_at, BUSINESS_TIMEZONE)
                  : 'Mark reviewed'}
              </button>
            </label>
          </div>
        ) : (
          <div className="mt-4 flex flex-wrap items-center gap-2">
            <button
              type="button"
              disabled={busy || store.mode === 'manual' || !store.enabled}
              onClick={() => onScrape('full', only)}
              className="min-h-11 rounded-btn bg-navy px-4 text-sm font-semibold text-white disabled:opacity-40"
            >
              {busy ? 'Queued…' : only.trim() ? 'Crawl these' : 'Crawl now'}
            </button>
            {/* 06 §5's crawls are nightly, which makes "the vanity rail is empty" a wait until
              tomorrow unless something can ask for one category now. The crawler has always
              supported it; this is the first control that does. */}
            <input
              value={only}
              onChange={(event) => setOnly(event.target.value)}
              placeholder="only these categories…"
              aria-label={`Limit the crawl of ${store.name} to categories matching`}
              disabled={store.mode !== 'scrape' || !store.enabled}
              className="min-h-11 w-[15rem] max-w-full rounded-btn border border-line bg-white px-3 text-sm text-ink disabled:opacity-40"
            />
            <button
              type="button"
              disabled={busy || store.mode !== 'scrape' || !store.enabled}
              onClick={() => onScrape('prices')}
              className="min-h-11 rounded-btn border border-line bg-white px-4 text-sm font-semibold text-blue-deep disabled:opacity-40"
            >
              Refresh prices
            </button>
            <label className="flex items-center gap-2 text-xs text-muted">
              <span>Terms reviewed</span>
              <button
                type="button"
                onClick={() => void update.mutateAsync({ key: store.key, tos_reviewed: true })}
                className="min-h-10 rounded-btn border border-line bg-white px-3 text-xs font-semibold text-ink"
              >
                {store.tos_reviewed_at
                  ? shortDateTime(store.tos_reviewed_at, BUSINESS_TIMEZONE)
                  : 'Mark reviewed'}
              </button>
            </label>
          </div>
        )}

        {store.mode === 'feed' && (
          <form
            className="mt-3 flex flex-wrap gap-2"
            onSubmit={(event) => {
              event.preventDefault()
              void update.mutateAsync({ key: store.key, feed_url: feed })
            }}
          >
            <input
              value={feed}
              onChange={(event) => setFeed(event.target.value)}
              placeholder="Product feed URL"
              aria-label={`Feed URL for ${store.name}`}
              className="min-h-11 flex-1 rounded-btn border border-line bg-white px-3 text-sm text-ink"
            />
            <button
              type="submit"
              className="min-h-11 rounded-btn border border-line bg-white px-4 text-sm font-semibold text-blue-deep"
            >
              Save feed
            </button>
          </form>
        )}
      </div>
    </article>
  )
}

function RunTable({ runs }: { runs: ScrapeRun[] }) {
  return (
    <>
      <h2 className="mt-10 text-xl">Recent crawls</h2>
      <p className="mt-1 text-sm text-muted">
        Newest first. A crawl with no result never finished — its worker was restarted or ran out of
        memory, and the nightly check closes it out.
      </p>
      <div className="mt-3 overflow-x-auto rounded-card border border-line bg-white">
        <table className="w-full min-w-[900px] border-collapse text-sm">
          <thead>
            <tr className="border-b border-line text-left">
              {[
                'Store',
                'Kind',
                'Started',
                'Took',
                'Pages',
                'Products',
                'New',
                'Prices',
                'Result',
              ].map((heading) => (
                <th
                  key={heading}
                  className="p-3 font-mono text-xs uppercase tracking-[0.1em] text-muted"
                >
                  {heading}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {runs.map((run) => (
              <tr key={run.id} className="border-b border-line align-top last:border-0">
                <td className="p-3 font-semibold text-ink">{run.store_key}</td>
                <td className="p-3 text-muted">{run.kind}</td>
                <td className="p-3 whitespace-nowrap text-muted">
                  {shortDateTime(run.started_at, BUSINESS_TIMEZONE)}
                </td>
                <td className="p-3 whitespace-nowrap tabular-nums text-muted">{duration(run)}</td>
                <td className="p-3 tabular-nums">{run.pages}</td>
                <td className="p-3 tabular-nums">{run.products_seen}</td>
                <td className="p-3 tabular-nums">{run.products_new}</td>
                <td className="p-3 tabular-nums">{run.prices_changed}</td>
                <td className="p-3">
                  <Status run={run} />
                  {run.errors.length > 0 && <Errors errors={run.errors} />}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  )
}

/** Grouped rather than listed: see the module docstring. */
function Errors({ errors }: { errors: { stage?: string; error?: string; url?: string }[] }) {
  const grouped = new Map<string, number>()
  for (const entry of errors) {
    const stage = entry.stage ?? 'unknown'
    grouped.set(stage, (grouped.get(stage) ?? 0) + 1)
  }
  const summary = [...grouped.entries()]
    .sort((a, b) => b[1] - a[1])
    .map(([stage, n]) => `${n}× ${STAGE_HELP[stage] ?? stage}`)
    .join(' · ')

  return (
    <details className="mt-1.5">
      <summary className="cursor-pointer text-xs text-muted">{summary}</summary>
      <ul className="m-0 mt-1.5 max-w-[44ch] list-none space-y-1 p-0 text-xs text-muted">
        {errors.slice(0, 6).map((entry, index) => (
          <li key={index} className="truncate" title={`${entry.url ?? ''} — ${entry.error ?? ''}`}>
            {entry.error}
          </li>
        ))}
        {errors.length > 6 && <li className="text-muted">…and {errors.length - 6} more</li>}
      </ul>
    </details>
  )
}

function duration(run: ScrapeRun): string {
  if (!run.finished_at) return '—'
  const ms = new Date(run.finished_at).getTime() - new Date(run.started_at).getTime()
  if (ms < 0) return '—'
  const minutes = Math.floor(ms / 60_000)
  if (minutes < 1) return `${Math.round(ms / 1000)}s`
  if (minutes < 60) return `${minutes}m`
  return `${Math.floor(minutes / 60)}h ${minutes % 60}m`
}

function Fact({
  label,
  value,
  note,
  tone = 'idle',
}: {
  label: string
  value: string
  note?: string
  tone?: Tone
}) {
  return (
    <div>
      <dt className="font-mono text-xs uppercase tracking-[0.1em] text-muted">{label}</dt>
      <dd
        className={`m-0 font-semibold tabular-nums ${tone === 'idle' ? 'text-ink' : TONE_TEXT[tone]}`}
      >
        {value}
      </dd>
      {note && <dd className="m-0 text-xs text-muted">{note}</dd>}
    </div>
  )
}

function Status({ run }: { run: ScrapeRun }) {
  const tone: Tone = !run.finished_at
    ? 'idle'
    : run.status === 'ok'
      ? 'good'
      : run.status === 'failed'
        ? 'bad'
        : 'warn'
  const label = !run.finished_at ? 'no result' : run.status
  return (
    <span
      className={`inline-block rounded-pill px-2.5 py-0.5 text-xs font-semibold ${TONE_PILL[tone]}`}
    >
      {label}
    </span>
  )
}
